/*
  Keyboard and screen reader behaviour for the header menus and buttons, mobile menu, Klaviyo popups, carousels and frames.
  Part of the talalighting.com WCAG 2.1 AA remediation; see the change log for each task.
*/
(function () {
  'use strict';

  function isVisible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  // 1.1 Header submenus: a disclosure button beside each top-level link.
  // Enter/Space toggles the panel, Escape closes it and returns focus to the button. Hover still works as before.
  function initMenuToggles() {
    var toggles = Array.prototype.slice.call(document.querySelectorAll('.js-menu-toggle'));
    if (!toggles.length) return;

    function setOpen(toggle, open) {
      var item = toggle.closest('.menu__item');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (item) item.classList.toggle('is-open', open);
    }

    function closeAll(except) {
      toggles.forEach(function (toggle) {
        if (toggle !== except) setOpen(toggle, false);
      });
    }

    toggles.forEach(function (toggle) {
      var item = toggle.closest('.menu__item');
      if (!item) return;

      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        closeAll(toggle);
        setOpen(toggle, open);
      });

      item.addEventListener('keydown', function (event) {
        if ((event.key === 'Escape' || event.key === 'Esc') && toggle.getAttribute('aria-expanded') === 'true') {
          event.stopPropagation();
          setOpen(toggle, false);
          toggle.focus();
        }
      });

      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) setOpen(toggle, false);
      });

      // a panel opened from the keyboard shouldn't stay open under another one opened by hover
      item.addEventListener('mouseenter', function () {
        closeAll(toggle);
      });
    });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('.menu__item')) closeAll();
    });
  }

  // 1.6 Klaviyo popup forms. Klaviyo already renders role="dialog" aria-modal="true", moves focus in and closes on Escape.
  // Added here: an accessible name from the popup's heading (Klaviyo's is "POPUP Form"), an Escape fallback, and focus
  // back to where the visitor was when it closes (with nothing focused before, Klaviyo sends it to the Cookiebot widget).
  function initKlaviyoPopups() {
    var DIALOG = '[role="dialog"][aria-modal="true"]';
    var openDialog = null;
    var lastPageFocus = null;
    var titleCount = 0;
    var scheduled = false;

    function findOpenDialog() {
      var dialogs = document.querySelectorAll(DIALOG);
      for (var i = 0; i < dialogs.length; i++) {
        if (dialogs[i].querySelector('.klaviyo-close-form') && isVisible(dialogs[i])) return dialogs[i];
      }
      return null;
    }

    function nameDialog(dialog) {
      if (dialog.getAttribute('aria-labelledby')) return;
      var heading = dialog.querySelector('h1, h2, h3, h4, [role="heading"]');
      if (heading && heading.textContent.trim()) {
        if (!heading.id) heading.id = 'KlaviyoPopupTitle-' + (++titleCount);
        dialog.setAttribute('aria-labelledby', heading.id);
        return;
      }
      // No text heading: some popups have their title as an image (the shipping location popup's is "Choose shipping
      // location"), so use the first image's alt, outside the buttons, in place of Klaviyo's generic "POPUP Form"
      if (!/^\w+ form$/i.test(dialog.getAttribute('aria-label') || '')) return;
      var images = dialog.querySelectorAll('.klaviyo-form-image img[alt]');
      for (var i = 0; i < images.length; i++) {
        var alt = images[i].getAttribute('alt').trim();
        if (alt && !images[i].closest('button, a')) {
          dialog.setAttribute('aria-label', alt);
          return;
        }
      }
    }

    function restoreFocus() {
      var active = document.activeElement;
      var lost = !active || active === document.body || !!(active.closest && active.closest('#CookiebotWidget'));
      if (!lost) return;
      if (lastPageFocus && document.contains(lastPageFocus) && isVisible(lastPageFocus)) {
        lastPageFocus.focus();
      } else {
        // nothing was focused before the popup opened: go back to the start of the page, so the next Tab reaches the skip link
        if (!document.body.hasAttribute('tabindex')) document.body.setAttribute('tabindex', '-1');
        document.body.focus();
      }
    }

    function check() {
      scheduled = false;
      var dialog = findOpenDialog();
      if (dialog && dialog !== openDialog) {
        openDialog = dialog;
        nameDialog(dialog);
      } else if (!dialog && openDialog) {
        openDialog = null;
        // Klaviyo moves focus after it unmounts the form, so look once it has finished
        setTimeout(restoreFocus, 150);
      }
    }

    document.addEventListener('focusin', function (event) {
      var target = event.target;
      if (!target.closest || target.closest(DIALOG) || target.closest('#CookiebotWidget')) return;
      lastPageFocus = target;
    });

    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(check);
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('keydown', function (event) {
      if ((event.key !== 'Escape' && event.key !== 'Esc') || !openDialog) return;
      var dialog = openDialog;
      setTimeout(function () {
        if (!isVisible(dialog)) return;
        var close = dialog.querySelector('.klaviyo-close-form');
        if (close) close.click();
      }, 100);
    });
  }

  // 1.7 Mobile menu. header.js opens and closes it by toggling `mobile-menu-is-open`; this keeps aria-expanded in sync,
  // keeps the closed (off-screen) menu out of the tab order with inert, moves focus to the first link on open,
  // closes on Escape and returns focus to the button that opened it.
  function initMobileMenu() {
    var menu = document.querySelector('.js-mobile-menu');
    var openers = Array.prototype.slice.call(document.querySelectorAll('.js-mobile-menu-open'));
    if (!menu || !openers.length) return;

    var isOpen = menu.classList.contains('mobile-menu-is-open');
    var opener = null;

    function sync() {
      menu.inert = !isOpen;
      openers.forEach(function (button) {
        button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }
    sync();

    openers.forEach(function (button) {
      button.addEventListener('click', function () {
        opener = button;
      });
    });

    new MutationObserver(function () {
      var open = menu.classList.contains('mobile-menu-is-open');
      if (open === isOpen) return;
      isOpen = open;
      sync();
      if (open) {
        var links = Array.prototype.slice.call(menu.querySelectorAll('a[href]'));
        var first = links.filter(isVisible)[0];
        if (first) first.focus();
      } else {
        var target = opener && isVisible(opener) ? opener : openers.filter(isVisible)[0];
        if (target && (menu.contains(document.activeElement) || document.activeElement === document.body)) target.focus();
        opener = null;
      }
    }).observe(menu, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('keydown', function (event) {
      if ((event.key !== 'Escape' && event.key !== 'Esc') || !isOpen) return;
      if (window.Moonbase && Moonbase.header && typeof Moonbase.header.closeMobileMenu === 'function') {
        Moonbase.header.closeMobileMenu();
      } else {
        var close = menu.querySelector('.js-mobile-menu-toggle');
        if (close) close.click();
      }
    });
  }

  // 3.7 Moving content that starts on its own (WCAG 2.2.2): videos and the announcement bar get a pause/play button,
  // and with "reduce motion" set they start paused.
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var ICON_PAUSE = '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 12 12"><rect x="1.5" y="1" width="3" height="10" fill="currentColor"/><rect x="7.5" y="1" width="3" height="10" fill="currentColor"/></svg>';
  var ICON_PLAY = '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 12 12"><path d="M2.5 1l8 5-8 5z" fill="currentColor"/></svg>';
  var videoButtons = [];

  // A looping or autoplaying video without its own controls gets a button in its bottom-right corner. A video the visitor
  // paused stays paused: section scripts that restart videos as they scroll back into view are undone.
  function setupVideo(video) {
    if (video.hasAttribute('data-a11y-video')) return;
    if (!video.autoplay && !video.loop) return;
    video.setAttribute('data-a11y-video', '');

    var held = false;
    var button = null;

    function render() {
      if (!button) return;
      button.innerHTML = video.paused ? ICON_PLAY : ICON_PAUSE;
      button.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
    }

    video.addEventListener('play', function () {
      if (held) {
        video.pause();
        return;
      }
      render();
    });
    video.addEventListener('pause', render);
    // sections with "click to play" toggle the video itself: keep whatever the visitor chose there
    video.addEventListener('click', function () {
      held = !video.paused;
    }, true);

    if (reduceMotion) {
      held = true;
      video.pause();
    }

    // native controls already have a pause button; a button can't go inside a link or button; clones are hidden copies
    if (video.controls || video.closest('a, button, .glide__slide--clone')) return;

    var parent = video.parentElement;
    var anchor = parent;
    if (window.getComputedStyle(parent).position === 'static') {
      var positioned = Array.prototype.some.call(parent.querySelectorAll('*'), function (el) {
        return el !== video && /absolute|fixed/.test(window.getComputedStyle(el).position);
      });
      if (positioned) {
        // making the parent the positioning box would move its other overlays: hang the button off a holder instead
        anchor = document.createElement('span');
        anchor.className = 'a11y-video-holder';
        parent.insertBefore(anchor, video.nextSibling);
      } else {
        parent.style.position = 'relative';
      }
    }

    button = document.createElement('button');
    button.type = 'button';
    button.className = 'a11y-video-toggle';
    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation(); // some video sections link the whole video
      if (video.paused) {
        held = false;
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
      } else {
        held = true;
        video.pause();
      }
    });
    if (anchor === parent) {
      parent.insertBefore(button, video.nextSibling);
    } else {
      anchor.appendChild(button);
    }
    render();
    button.hidden = !isVisible(video);
    videoButtons.push({ button: button, video: video });
  }

  // some sections show a different video (or none) on mobile: only show the button while its video is shown
  function initVideoControls() {
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        videoButtons.forEach(function (item) {
          item.button.hidden = !isVisible(item.video);
        });
      }, 200);
    });
  }

  // announcement-bar.js keeps its Glide instance on the section element. Pausing switches autoplay off in Glide's
  // settings as well, so hovering out of the bar or using the arrows doesn't restart it.
  function initAnnouncementPause() {
    var bar = document.querySelector('[data-section-type="announcement-bar"]');
    var button = bar && bar.querySelector('.js-announcement-pause');
    if (!button || bar.querySelectorAll('.glide__slide:not(.glide__slide--clone)').length < 2) return;
    var tries = 0;

    (function attach() {
      var glide = bar.glide;
      if (!glide) {
        if (++tries < 40) setTimeout(attach, 250);
        return;
      }
      var delay = glide.settings.autoplay;
      if (!delay) return;
      var paused = false;

      function set(pause) {
        paused = pause;
        if (pause) {
          glide.settings.autoplay = false;
          glide.pause();
        } else {
          glide.play(delay);
        }
        button.innerHTML = pause ? ICON_PLAY : ICON_PAUSE;
        button.setAttribute('aria-label', button.getAttribute(pause ? 'data-label-play' : 'data-label-pause'));
      }

      button.addEventListener('click', function () {
        set(!paused);
      });
      set(reduceMotion);
      button.hidden = false;
    })();
  }

  // 2.4 Frames without a title: Shopify's web pixel sandboxes (and any other hidden frame a script adds) get one.
  // 2.5 Glide carousel clones: copies of real slides for the endless loop. inert keeps their links out of the tab order
  // and aria-hidden keeps screen readers from reading every slide twice. Glide rebuilds clones on resize, so keep watching.
  // 3.7 Videos get their pause button here too, so videos added after load are covered.
  function initInjectedContent() {
    var supportsInert = 'inert' in HTMLElement.prototype;
    var scheduled = false;

    function run() {
      scheduled = false;

      var frames = document.querySelectorAll('iframe:not([title]), iframe[title=""]');
      for (var i = 0; i < frames.length; i++) {
        var frame = frames[i];
        if (/^web-pixel-sandbox/.test(frame.id || frame.name || '')) {
          frame.title = 'Shopify web pixel';
        } else if (frame.getAttribute('aria-hidden') === 'true') {
          frame.title = 'Hidden frame';
        }
      }

      var clones = document.querySelectorAll('.glide__slide--clone:not([aria-hidden="true"])');
      for (var j = 0; j < clones.length; j++) {
        clones[j].setAttribute('aria-hidden', 'true');
        clones[j].inert = true;
        if (!supportsInert) {
          var focusables = clones[j].querySelectorAll('a[href], button, input, select, textarea, [tabindex]');
          for (var k = 0; k < focusables.length; k++) focusables[k].setAttribute('tabindex', '-1');
        }
        // 3.1 the copies are decorative: the real slide carries the image's alt text
        var images = clones[j].querySelectorAll('img[alt]:not([alt=""])');
        for (var m = 0; m < images.length; m++) images[m].setAttribute('alt', '');
        // 3.6 the copies repeat every id in the slide (forms, inputs); ids inside SVGs stay so their clip paths still draw
        var ids = clones[j].querySelectorAll('[id]');
        for (var n = 0; n < ids.length; n++) {
          if (!ids[n].closest('svg')) ids[n].removeAttribute('id');
        }
        // 3.7 a copy of a slide cloned after its video got a pause button: drop the copied button, and keep the copy's
        // video still when "reduce motion" is set
        var copied = clones[j].querySelectorAll('.a11y-video-toggle, .a11y-video-holder');
        for (var q = 0; q < copied.length; q++) copied[q].parentNode.removeChild(copied[q]);
        if (reduceMotion) {
          var cloneVideos = clones[j].querySelectorAll('video');
          for (var r = 0; r < cloneVideos.length; r++) {
            cloneVideos[r].autoplay = false;
            cloneVideos[r].pause();
          }
        }
      }

      var videos = document.querySelectorAll('video:not([data-a11y-video])');
      for (var v = 0; v < videos.length; v++) setupVideo(videos[v]);
    }

    run();
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(run);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // 2.6 aria-expanded on the header Account, Search and Cart buttons, following the classes the theme already sets:
  // `is-active` on the account menu (header.js), `is-open` on the search modal (Micromodal) and on the cart drawer (GoCart).
  // The account menu also closes on Escape (focus back to its button) and when focus or a click leaves it.
  function initHeaderButtons() {
    function track(panel, openClass) {
      if (!panel || !panel.id) return;
      var buttons = Array.prototype.slice.call(document.querySelectorAll('[aria-controls="' + panel.id + '"]'));
      if (!buttons.length) return;
      function sync() {
        var open = panel.classList.contains(openClass);
        buttons.forEach(function (button) {
          button.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
      sync();
      new MutationObserver(sync).observe(panel, { attributes: true, attributeFilter: ['class'] });
    }

    track(document.getElementById('search_modal'), 'is-open');
    track(document.getElementById('section-ajax-cart'), 'is-open');

    Array.prototype.slice.call(document.querySelectorAll('.js-account-menu-btn')).forEach(function (item) {
      var menu = item.querySelector('.js-account-menu');
      var button = item.querySelector('button[aria-controls]');
      if (!menu || !button) return;
      track(menu, 'is-active');

      function close() {
        menu.classList.remove('is-active');
      }

      item.addEventListener('keydown', function (event) {
        if ((event.key === 'Escape' || event.key === 'Esc') && menu.classList.contains('is-active')) {
          event.stopPropagation();
          close();
          button.focus();
        }
      });

      item.addEventListener('focusout', function (event) {
        if (event.relatedTarget && !item.contains(event.relatedTarget)) close();
      });

      document.addEventListener('click', function (event) {
        if (!item.contains(event.target)) close();
      });
    });
  }

  // 3.5 Mobile filters drawer. globalDrawers.js opens and closes it by toggling `is-open`; this keeps aria-expanded on the
  // Filters button in sync, keeps the closed (off-screen) drawer out of the tab order with inert, moves focus to the
  // close button on open and back to the Filters button on close, and closes it on Escape.
  function initFiltersDrawer() {
    var drawer = document.getElementById('filters-container');
    var trigger = document.querySelector('.js-drawer-trigger[aria-controls="filters-container"]');
    if (!drawer || !trigger) return;
    var close = drawer.querySelector('.js-drawer-drawer-close');
    var isOpen = drawer.classList.contains('is-open');

    function sync() {
      drawer.inert = !isOpen;
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
    sync();

    new MutationObserver(function () {
      var open = drawer.classList.contains('is-open');
      if (open === isOpen) return;
      isOpen = open;
      sync();
      if (open) {
        if (close) close.focus();
      } else if (drawer.contains(document.activeElement) || document.activeElement === document.body) {
        trigger.focus();
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('keydown', function (event) {
      if ((event.key === 'Escape' || event.key === 'Esc') && isOpen && close) close.click();
    });
  }

  function init() {
    initMenuToggles();
    initKlaviyoPopups();
    initMobileMenu();
    initInjectedContent();
    initHeaderButtons();
    initFiltersDrawer();
    initVideoControls();
    initAnnouncementPause();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
